import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { ContactIcon } from "@/components/icons";

import Button from "@/components/buttons/Button";
import SelectInput from "@/components/form-controls/SelectInput";
import TextInput from "@/components/form-controls/TextInput";
import Modal from "@/components/modal";
import toast from "react-hot-toast";

interface IProps {
    isOpen: boolean;
    onClose: (value: boolean) => void;
    onAddShareholder: (data: ShareholderFormData) => void;
    editData?: ShareholderFormData | null;
    showPositionInput?: boolean;
}

export interface ShareholderFormData {
    id: string;
    salutation: string;
    firstName: string;
    middleName: string;
    lastName: string;
    nationality: string;
    numberOfShares: number;
    shareholderPosition?: string;
}

export default function ShareHolderModal({ isOpen, onClose, onAddShareholder, editData, showPositionInput = false }: IProps) {
    const methods = useForm<ShareholderFormData>({
        defaultValues: {
            id: "",
            salutation: "",
            firstName: "",
            middleName: "",
            lastName: "",
            nationality: "",
            numberOfShares: 0,
            shareholderPosition: "",
        },
    });

    const { register, setValue, reset, formState: { errors } } = methods;

    useEffect(() => {
        if (editData) {
            Object.entries(editData).forEach(([key, value]) => {
                setValue(key as keyof ShareholderFormData, value);
            });
        } else {
            reset(); 
        }
    }, [editData, setValue, reset]);

    const handleAddOrUpdate = () => {
        const data = methods.getValues(); 
        if (!data.salutation || !data.firstName || !data.lastName || !data.nationality || !data.numberOfShares) {
            toast.error("Please fill in all required fields.");
            return;
        }
        const stakeholderData: ShareholderFormData = {
            ...data,
            id: editData?.id || crypto.randomUUID(), 
        };
        if (editData) {
            onAddShareholder(stakeholderData);
        } else {
            onAddShareholder(stakeholderData);
        }
    
        reset(); 
        toast.success(
            `${editData ? "Updated" : "Added"} stakeholder: ${stakeholderData.firstName} ${stakeholderData.lastName} successfully`
        );
        // onClose(false); 
    };
    


    

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <div className="absolute top-4 left-6 border border-[#E4E7EC] rounded-lg p-2.5">
                <ContactIcon/>
            </div>
            <FormProvider {...methods}>
                <div className="grid grid-cols-2 gap-4 w-full p-1 mt-2">
                    <div className="col-span-2">
                        <h1 className="text-[#101828] text-lg">
                            {editData ? "Edit" : "Add"} Stakeholder
                        </h1>
                        <span className="text-[#475467] text-xs">
                            {editData ? "Modify" : "Add"} people involved in your business
                        </span>
                    </div>

                    <SelectInput
                        label="Salutation"
                        displayName="label"
                        valueName="value"
                        error={errors.salutation?.message?.toString()}
                        register={register("salutation", { required: "Salutation is required" })}
                        options={[
                            { label: "Mr", value: "mr" },
                            { label: "Mrs", value: "mrs" },
                            { label: "Miss", value: "miss" },
                        ]}
                        required
                    />

                    <TextInput
                        label="First Name"
                        error={errors.firstName?.message?.toString()}
                        placeholder="Enter customer first name"
                        register={register("firstName", { required: "First Name is required" })}
                        required
                    />

                    <TextInput
                        label="Middle Name"
                        error={errors.middleName?.message?.toString()}
                        placeholder="Enter customer middle name"
                        register={register("middleName", { required: "Middle Name is required" })}
                        required
                    />

                    <TextInput
                        label="Last Name"
                        error={errors.lastName?.message?.toString()}
                        placeholder="Enter customer last name"
                        register={register("lastName", { required: "Last Name is required" })}
                        required
                    />

                    <SelectInput
                        label="Nationality"
                        displayName="label"
                        error={errors.nationality?.message?.toString()}
                        valueName="value"
                        options={[
                            { label: "Tanzanian", value: "Tanzanian" },
                            { label: "Kenyan", value: "Kenyan" },
                            { label: "Ugandan", value: "Ugandan" },
                            { label: "Rwandan", value: "Rwandan" },
                        ]}
                        register={register("nationality", { required: "Nationality is required" })}
                        required
                        className={`${showPositionInput ? "col-span-1" : "col-span-2"}`}
                    />

                    {
                        showPositionInput && (
                            <TextInput
                                label="Postion"
                                error={errors.shareholderPosition?.message?.toString()}
                                placeholder="Enter stakeholder position"
                                register={register("shareholderPosition", { required: "Position is required" })}
                                required
                        />
                        )
                    }

                 

                    <TextInput
                        label="Number of Shares"
                        error={errors.numberOfShares?.message?.toString()}
                        placeholder="Enter stakeholder number of shares"
                        register={register("numberOfShares", {
                            required: "Number of shares is required",
                            valueAsNumber: true,
                        })}
                        className="col-span-2"
                        required
                    />

                    <div className="col-span-2">
                        <div className="flex justify-between items-center w-full gap-3">
                            <Button className="w-full" size="md" intent="default" onClick={() => onClose(false)}>
                                Cancel
                            </Button>
                            <Button
                                className="w-full"
                                size="md"
                                intent="primary"
                                type="button"
                                onClick={handleAddOrUpdate}
                            >
                                {editData ? "Update" : "Add"} Stakeholder
                            </Button>
                        </div>
                    </div>
                </div>
            </FormProvider>
        </Modal>
    );
}
